<script setup>
import {computed, onMounted} from 'vue'
import {useComponentsStore} from '@/stores/components'
import {useUserStore} from '@/stores/user'
import {useModalsStore} from '@/stores/modals'
import {useViewStore} from '@/stores/view'
import {useAppStore} from '@/stores/appStore'

// Components
import OdpCourseDatesCoursesMenu from '@/bespoke/odp/OdpCourseDatesCoursesMenu'
import OdpCourseDatesSchoolsMenu from '@/bespoke/odp/OdpCourseDatesSchoolsMenu'
import ListingOptionsSearchModalBtn from '@/common/listing/ListingOptionsSearchModalBtn'
import SearchFiltersResetBtn from '@/common/listing/SearchFiltersResetBtn'
import ListingOptionsPaginationMetrics from '@/common/listing/ListingOptionsPaginationMetrics'
import ListingOptionsPreviousBtn from '@/common/listing/ListingOptionsPreviousBtn'
import ListingOptionsNextBtn from '@/common/listing/ListingOptionsNextBtn'
import ListingOptionsMenu from '@/common/listing/ListingOptionsMenu'
import TableView from '@/common/listing/TableView'
import GridView from '@/common/listing/GridView'
import Button from "@/common/Button.vue"
import SearchFilterToolbar from "@/common/search-filters/SearchFilterToolbar.vue"

const props = defineProps({
    options: {
        type: Object,
        required: true
    }
})

defineEmits(['hide-modal'])

// Pinia Stores
const appStore = useAppStore()
const componentsStore = useComponentsStore()
const userStore = useUserStore()
const modalsStore = useModalsStore()

// --- State Destructuring ---
const {formId, listingName, onSelect, selectedIds, hiddenFilters} = props.options

// --- Computed View ID & Store Instance ---

const viewId = computed(() => {
    if (formId === 'pageEditorForm') {
        return 'view:pageEditorSelectListModal'
    }
    return 'view:' + listingName
})

const viewStore = computed(() => useViewStore(viewId.value))

// --- Computed State ---

const view = computed(() => viewStore.value || {})

const tableName = computed(() => view.value.tableName || '')

const viewMode = computed(() => view.value.viewMode)

const addNew = computed(() => {
    if (!view.value.componentId) return false
    const component = componentsStore.items.find(o => o.id === view.value.componentId)
    return component ? !!component.addNew : false
})

const show = computed(() => {
    return (
        userStore.access(tableName.value, 'add')
        && !view.value.showArchive
        && tableName.value !== 'm_basket_orders'
        && addNew.value
    )
})

const isOdp = computed(() => {
    const sitename = appStore.sitename || ''
    const table = tableName.value || ''
    return (
        sitename.indexOf('outdoorsproject') === 0 &&
        table.indexOf('odp_course_dates') === 0
    )
})

// --- Methods ---

const showAddItemModal = () => {
    modalsStore.show({
        componentName: 'AddItemModal',
        obj: {
            tableName: tableName.value,
            onAddItem: (obj) => {
                // Logic for adding item to selection can be placed here
            }
        }
    })
}

// --- Lifecycle ---

onMounted(() => {
    viewStore.value.init({
        listingName,
        selectedIds,
        hiddenFilters,
        selectMode: true,
        onSelect
    })
})
</script>

<template>
    <div class="modal-dialog modal-xl">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">Select an item</h4>
                <button type="button" class="btn-close" @click="$emit('hide-modal')" aria-label="Close"></button>
            </div>

            <div v-if="view.tableName" class="modal-body p-0">
                <header>
                    <div class="d-flex border-bottom">
                        <div class="flex-fill">
                            <Button v-if="show" class="rounded-0" @click="showAddItemModal">
                                <i class="bi-plus-lg"/>
                            </Button>

                            <ListingOptionsSearchModalBtn :view-id="viewId"/>
                            <SearchFiltersResetBtn :view-id="viewId"/>
                        </div>

                        <div class="d-flex align-items-center">
                            <ListingOptionsPaginationMetrics :view-id="viewId"/>
                            <ListingOptionsPreviousBtn class="ms-1" :view-id="viewId"/>
                            <ListingOptionsNextBtn class="ms-1" :view-id="viewId"/>
                            <ListingOptionsMenu class="ms-1" :view-id="viewId"/>
                        </div>
                    </div>

                    <SearchFilterToolbar :view-id="viewId"/>
                </header>

                <div v-if="isOdp">
                    <OdpCourseDatesCoursesMenu :view-id="viewId"/>
                    <OdpCourseDatesSchoolsMenu :view-id="viewId"/>
                </div>

                <TableView v-if="viewMode === 'table' || !viewMode" :view-id="viewId" class="m-3"/>
                <GridView v-else-if="viewMode === 'thumbnail'" :view-id="viewId" class="m-3"/>
            </div>

            <div v-else class="modal-body p-5 text-center">
                <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading listing...</span>
                </div>
            </div>
        </div>
    </div>
</template>